import { create } from 'zustand';
import { getTenants } from '../api/generated/tenants/tenants';
import type {
  FacilityFlowCoreModulesTenantsDtosTenantDto,
  FacilityFlowCoreModulesTenantsDtosCreateTenantRequest,
  FacilityFlowCoreModulesTenantsDtosUpdateTenantRequest,
  FacilityFlowCoreModulesTenantsEntitiesBusinessPlan,
} from '../api/generated/models';

interface TenantState {
  tenants: FacilityFlowCoreModulesTenantsDtosTenantDto[];
  loading: boolean;
  error?: string;
}

interface TenantStore extends TenantState {
  fetchTenants: () => Promise<void>;
  createTenant: (request: FacilityFlowCoreModulesTenantsDtosCreateTenantRequest) => Promise<void>;
  updateTenant: (id: string, request: FacilityFlowCoreModulesTenantsDtosUpdateTenantRequest) => Promise<void>;
  deactivateTenant: (id: string) => Promise<void>;
}

const {
  getApiV1Tenants,
  postApiV1Tenants,
  putApiV1TenantsId,
  patchApiV1TenantsIdDeactivate,
} = getTenants();

export const useTenantStore = create<TenantStore>((set, get) => ({
  tenants: [],
  loading: false,
  error: undefined,

  fetchTenants: async () => {
    set({ loading: true, error: undefined });
    try {
      const response = await getApiV1Tenants();
      set({ tenants: response || [], loading: false });
    } catch {
      set({
        error: 'Fehler beim Laden der Tenants',
        loading: false,
      });
    }
  },

  createTenant: async (request) => {
    set({ loading: true, error: undefined });
    try {
      await postApiV1Tenants(request);
      await get().fetchTenants();
      set({ loading: false });
    } catch {
      set({
        error: 'Fehler beim Erstellen des Tenants',
        loading: false,
      });
      throw new Error('Create failed');
    }
  },

  updateTenant: async (id, request) => {
    set({ loading: true, error: undefined });
    try {
      await putApiV1TenantsId(id, request);
      await get().fetchTenants();
      set({ loading: false });
    } catch {
      set({
        error: 'Fehler beim Aktualisieren des Tenants',
        loading: false,
      });
      throw new Error('Update failed');
    }
  },

  deactivateTenant: async (id) => {
    set({ loading: true, error: undefined });
    try {
      await patchApiV1TenantsIdDeactivate(id);
      await get().fetchTenants();
      set({ loading: false });
    } catch {
      set({
        error: 'Fehler beim Deaktivieren des Tenants',
        loading: false,
      });
      throw new Error('Deactivate failed');
    }
  },
}));

export { FacilityFlowCoreModulesTenantsEntitiesBusinessPlan as BusinessPlan };
